import { useState, useEffect } from 'react';
import { Sparkles, Loader2, Save, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { aiApi } from '@/services/aiApi';

export function AISettingsPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providerBaseUrl, setProviderBaseUrl] = useState('https://api.openai.com/v1');
  const [modelName, setModelName] = useState('gpt-4o-mini');
  const [apiKey, setApiKey] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [customHeaders, setCustomHeaders] = useState<Record<string, string>>({
    'User-Agent': 'Claude Code',
  });
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await aiApi.getUserAIConfig();
      const data = res.data.data;
      setProviderBaseUrl(data.providerBaseUrl);
      setModelName(data.modelName);
      setIsEnabled(data.isEnabled);
      setHasApiKey(data.hasApiKey);
      setCustomHeaders(data.customHeaders || { 'User-Agent': 'Claude Code' });
    } catch {
      toast({ title: '获取 AI 配置失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: {
        providerBaseUrl: string;
        modelName: string;
        isEnabled: boolean;
        customHeaders: Record<string, string>;
        apiKey?: string;
      } = {
        providerBaseUrl,
        modelName,
        isEnabled,
        customHeaders,
      };
      if (apiKey.trim()) {
        payload.apiKey = apiKey.trim();
      }
      await aiApi.updateUserAIConfig(payload);
      toast({ title: 'AI 配置已保存' });
      setApiKey('');
      setHasApiKey(true);
    } catch {
      toast({ title: '保存失败', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">AI 设置</h1>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            AI Provider 配置
          </CardTitle>
          <CardDescription>
            平台不统一提供 AI Key，请填写你自己的 OpenAI 兼容 API Key。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="ai-enabled">启用 AI 优化</Label>
              <p className="text-xs text-gray-500">开启后可在写笔记时使用 AI 分析</p>
            </div>
            <Switch id="ai-enabled" checked={isEnabled} onCheckedChange={setIsEnabled} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="provider-url">Provider Base URL</Label>
            <Input
              id="provider-url"
              value={providerBaseUrl}
              onChange={(e) => setProviderBaseUrl(e.target.value)}
              placeholder="https://api.openai.com/v1"
            />
            <p className="text-xs text-gray-500">
              支持 OpenAI 兼容格式的 API 地址。常见：OpenAI https://api.openai.com/v1；Kimi https://api.moonshot.cn/v1；DeepSeek https://api.deepseek.com/v1
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="model-name">模型名称</Label>
            <Input
              id="model-name"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              placeholder="gpt-4o-mini"
            />
            <p className="text-xs text-gray-500">
              常见：gpt-4o-mini、moonshot-v1-8k、deepseek-chat
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="api-key">API Key{hasApiKey && <span className="text-green-600 ml-1">（已配置）</span>}</Label>
            <div className="relative">
              <Input
                id="api-key"
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={hasApiKey ? '留空则保持原 Key 不变' : '请输入你的 API Key'}
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500">Key 将加密存储，仅用于调用你配置的 Provider</p>
          </div>

          <div className="space-y-2">
            <Label>自定义请求头</Label>
            <p className="text-xs text-gray-500">
              部分 Provider（如 Kimi For Coding）会校验请求来源。默认已设置 User-Agent: Claude Code，你可按需调整。
            </p>
            <div className="space-y-2">
              {Object.entries(customHeaders).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <span className="px-2 py-1 bg-gray-100 rounded text-gray-700 font-mono">{key}</span>
                  <span className="text-gray-500">=</span>
                  <span className="flex-1 truncate text-gray-700 font-mono">{value}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = { ...customHeaders };
                      delete next[key];
                      setCustomHeaders(next);
                    }}
                    className="text-gray-400 hover:text-red-500"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={headerKey}
                onChange={(e) => setHeaderKey(e.target.value)}
                placeholder="Header 名"
                className="text-sm"
              />
              <Input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                placeholder="Header 值"
                className="text-sm flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (headerKey.trim() && headerValue.trim()) {
                    setCustomHeaders({ ...customHeaders, [headerKey.trim()]: headerValue.trim() });
                    setHeaderKey('');
                    setHeaderValue('');
                  }
                }}
              >
                添加
              </Button>
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存配置
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
